import {noop} from 'lodash';
import React, {
  createContext,
  PropsWithChildren,
  RefObject,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

/*
 * Props.
 */

export interface TooltipOrchestratorContextProps {
  onTooltipHide(id: string): void;
  onTooltipShow(id: string, ref: RefObject<HTMLElement>): void;
  canShowTooltip(): boolean;
}

export const TooltipOrchestratorContext = createContext<TooltipOrchestratorContextProps>({
  onTooltipHide: noop,
  onTooltipShow: noop,
  canShowTooltip: () => true,
});

export function useTooltipOrchestrator() {
  return useContext(TooltipOrchestratorContext);
}

export const TooltipOrchestrator = (props: PropsWithChildren<{}>) => {
  // We're using a state and a ref to eventually end up in the right state when two tooltips are shown by the same mouse/hover event.
  const [canEventuallyShowTooltip, setCanEventuallyShowTooltip] = useState(true);
  const tooltipAnchorsForIds = useRef<Map<string, HTMLElement | null>>(new Map());

  const canShowTooltip = useCallback(() => {
    // Checking if the anchors of the tooltips are still in the DOM. This is needed because *sometimes* React doesn't call the effect cleanup of `TooltipCoordinator` on unmount, leading to "stray" tooltips IDs. This ensures this can't happen by double-checking that the anchors we know about are still in the DOM.
    const areAllAnchorsVisible = Array.from(tooltipAnchorsForIds.current.values()).every((e) =>
      Boolean(e?.closest('html')),
    );

    if (!areAllAnchorsVisible) {
      tooltipAnchorsForIds.current.clear();
    }

    if (!canEventuallyShowTooltip) {
      return tooltipAnchorsForIds.current.size < 1;
    }

    return canEventuallyShowTooltip && tooltipAnchorsForIds.current.size < 1;
  }, [canEventuallyShowTooltip]);
  const value: TooltipOrchestratorContextProps = useMemo(
    () => ({
      canShowTooltip,
      onTooltipHide: (id) => {
        tooltipAnchorsForIds.current.delete(id);
        if (tooltipAnchorsForIds.current.size < 1) {
          setCanEventuallyShowTooltip(true);
        }
      },
      onTooltipShow: (id, ref) => {
        tooltipAnchorsForIds.current.set(id, ref.current);
        if (tooltipAnchorsForIds.current.size >= 1) {
          setCanEventuallyShowTooltip(false);
        }
      },
    }),
    [canShowTooltip],
  );

  return (
    <TooltipOrchestratorContext.Provider value={value}>{props.children}</TooltipOrchestratorContext.Provider>
  );
};
